import React, { useEffect, useState, useRef } from 'react';
import { useScrollLockClass } from '../../hooks/useScrollLockClass';
import { Post } from '../../types/Post';
import { getMonsterImgPath } from '../../utils/monster';

import { monthDayTime } from '../../utils/date';
import MonsterSelect from '../Desktop/MonsterSelect';
import { isOperator } from '../../utils/permission';
import { IconLink } from '../../components/Icons';
import BackButton from '../../components/BackButton';
import { loadMonsterMapping } from '../../utils/monsterMapping';
import { TacticBuild, BuildRunes, hasBuild } from '../TacticBuild';
import { openRuneFit } from '../../utils/fitHandoff';
import { useBackHandler } from '../../hooks/useBackHandler';
import ExtraDefenseRows, { ImportDefenseButton } from '../Common/ExtraDefenses';

interface Monster {
  name: string;
  attribute: string;
}

interface TacticPostProps {
  open: boolean;
  onClose: () => void;
  tactic: Post | null;
  monsterMapping: Record<string, any>;
  user: { uuid: string; nickname: string; role: string; profileImg: string };
  onTacticDeleted?: () => void;
}

const TacticPost: React.FC<TacticPostProps> = ({ open, onClose, tactic: initialTactic, monsterMapping: initialMonsterMapping, user, onTacticDeleted }) => {
  const [tactic, setTactic] = useState<Post | null>(initialTactic);
  /**
   * [공략] / [룬 정보] 탭. 룬 정보는 공덱 몹을 누르면 그 몹의 세팅을 전투기록 창처럼 보여 준다(사용자 지시).
   * 방덱은 룬 세팅을 적지 않는다.
   */
  const [tab, setTab] = useState<'tactic' | 'runes'>('tactic');
  const [runeIdx, setRuneIdx] = useState(0);
  /** 수정 화면의 공덱 세 칸 세팅 — 몹을 바꾸거나 빼면 그 칸은 지운다 */
  const [editBuilds, setEditBuilds] = useState<Array<TacticBuild | null>>([null, null, null]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editComment, setEditComment] = useState('');
  /** 수정 중인 호환 방덱 — [저장] 할 때 함께 보낸다 */
  const [editExtra, setEditExtra] = useState<any[]>([]);
  const [editAttackTeam, setEditAttackTeam] = useState<Monster[]>([]);
  const [editDefenseTeam, setEditDefenseTeam] = useState<Monster[]>([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [monsterSelectOpen, setMonsterSelectOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<'attack' | 'defense' | 'extra' | null>(null);
  /** 추가 방덱 줄 중 몇 번째를 고르는 중인지 */
  const [editingRow, setEditingRow] = useState(0);
  const [editingSlot, setEditingSlot] = useState<number>(0);
  const [monsterMapping, setMonsterMapping] = useState<Record<string, any>>(initialMonsterMapping || {});
  const [mappingLoaded, setMappingLoaded] = useState(false);


  // 모달 닫기 핸들러 (드롭다운도 함께 닫음)
  const handleClose = () => {
    setDropdownOpen(false);
    setEditMode(false);
    onClose();
  };

  // 뒤로 가기: 창 자체는 부모가 등록한다 — 여기서는 수정 모드·몹 고르기만
  const shown = open && !!tactic;
  useBackHandler(shown && editMode, () => { setEditMode(false); setEditError(''); });
  useBackHandler(shown && monsterSelectOpen, () => setMonsterSelectOpen(false));

  // 몬스터 매핑 로드
  useEffect(() => {
    if (open) {
      // 이미 매핑이 있으면 로드된 것으로 간주
      if (initialMonsterMapping && Object.keys(initialMonsterMapping).length > 0 && initialMonsterMapping.monsters) {
        setMonsterMapping(initialMonsterMapping);
        setMappingLoaded(true);
      } else if (!mappingLoaded) {
        // 매핑이 없으면 API에서 로드
        loadMonsterMapping()
          .then(data => {
            setMonsterMapping(data);
            setMappingLoaded(true);
          })
          .catch(error => {
            setMappingLoaded(true); // 에러가 나도 계속 진행
          });
      }
    } else {
      setMappingLoaded(false);
    }
  }, [open, initialMonsterMapping]);

  // 최신 공략 데이터 fetch
  useEffect(() => {
    if (open && initialTactic?._id) {
      fetch(`/api/tactics/${initialTactic._id}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          setTactic(data);
        });
    } else {
      setTactic(initialTactic);
      setDropdownOpen(false);
      setEditMode(false);
    }
  }, [open, initialTactic]);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // 유저 드롭다운 외부 클릭 핸들러
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);


  // 수정 버튼 클릭 시
  const handleEditClick = () => {
    setEditTitle(tactic?.title || '');
    setEditComment(tactic?.comment || '');
    setEditExtra((tactic as any)?.extraDefenses || []);
    // 3개씩 패딩
    const attack = [...(tactic?.attackTeam || [])];
    const defense = [...(tactic?.defenseTeam || [])];
    while (attack.length < 3) attack.push({ name: '', attribute: '' });
    while (defense.length < 3) defense.push({ name: '', attribute: '' });
    setEditAttackTeam(attack);
    setEditDefenseTeam(defense);
    setEditBuilds([0, 1, 2].map((i) => (tactic?.builds?.attack?.[i] as TacticBuild) || null));
    setEditMode(true);
    setDropdownOpen(false);
  };

  // 몬스터 선택 핸들러
  const handleMonsterSelect = (monster: any) => {
    const newMonster = { name: monster.name, attribute: monster.attribute };
    if (editingTeam === 'attack') {
      const newTeam = [...editAttackTeam];
      newTeam[editingSlot] = newMonster;
      setEditAttackTeam(newTeam);
      setEditBuilds((bs) => bs.map((b, i) => (i === editingSlot ? null : b)));
    } else if (editingTeam === 'extra') {
      setEditExtra((rows) => rows.map((r, i) => (i === editingRow ? r.map((m: any, j: number) => (j === editingSlot ? newMonster : m)) : r)));
    } else if (editingTeam === 'defense') {
      const newTeam = [...editDefenseTeam];
      newTeam[editingSlot] = newMonster;
      setEditDefenseTeam(newTeam);
    }
    setMonsterSelectOpen(false);
  };

  // 몬스터 삭제 핸들러
  const handleMonsterRemove = (team: 'attack' | 'defense', index: number) => {
    if (team === 'attack') {
      const newTeam = [...editAttackTeam];
      newTeam[index] = { name: '', attribute: '' };
      setEditAttackTeam(newTeam);
      setEditBuilds((bs) => bs.map((b, i) => (i === index ? null : b)));
    } else {
      const newTeam = [...editDefenseTeam];
      newTeam[index] = { name: '', attribute: '' };
      setEditDefenseTeam(newTeam);
    }
  };

  // 저장 버튼 클릭 시
  /**
   * 수정한 공덱 세팅 — 빈 칸을 걸러 내면 칸이 당겨지므로 **몹과 짝을 지은 채로** 거른다.
   * 방덱은 적지 않는다.
   */
  const editedBuilds = () => {
    const kept = editAttackTeam
      .map((m, i) => ({ m, b: editBuilds[i] || null }))
      .filter((x) => x.m.name && x.m.attribute)
      .map((x) => x.b);
    return { attack: [0, 1, 2].map((i) => kept[i] || null) };
  };

  const handleEditSave = async () => {
    if (!editTitle.trim()) {
      setEditError('제목을 입력하세요.');
      return;
    }
    // 빈 몬스터 제거
    const attackFiltered = editAttackTeam.filter(m => m.name && m.attribute);
    const defenseFiltered = editDefenseTeam.filter(m => m.name && m.attribute);
    setEditLoading(true);
    setEditError('');
    try {
      const res = await fetch(`/api/tactics/${tactic?._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editTitle,
          comment: editComment,
          // 셋이 다 찬 줄만
          extraDefenses: editExtra.filter((r) => r.every((m: any) => m.name && m.attribute)),
          attackTeam: attackFiltered,
          defenseTeam: defenseFiltered,
          builds: editedBuilds()
        })
      });
      const data = await res.json();
      if (data.success) {
        setTactic(data.tactic);
        setEditMode(false);
      } else {
        setEditError(data.error || '수정 실패');
      }
    } catch (e) {
      setEditError('수정 중 오류 발생');
    }
    setEditLoading(false);
  };

  // 공략 삭제 핸들러
  const handleDeleteTactic = async () => {
    if (!tactic?._id || !window.confirm('정말로 이 공략을 삭제하시겠습니까?')) return;

    try {
      const res = await fetch(`/api/tactics/${tactic._id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        alert('공략이 삭제되었습니다.');
        handleClose(); // 모달 닫기 (드롭다운도 함께 닫음)
        // 부모 컴포넌트에 삭제 완료 알림
        if (onTacticDeleted) {
          onTacticDeleted();
        }
      } else {
        const data = await res.json();
        alert(data.message || '삭제에 실패했습니다.');
      }
    } catch (error) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 창이 떠 있는 동안 뒤 페이지 스크롤 잠금 — 클래스로, 떠 있는 창 수를 세어 (hooks/useScrollLockClass — 인라인 값을 바꾸던 것이 목록의 잠금과 엉켜 풀리지 않았다)
  useScrollLockClass(open);

  if (!open || !tactic) return null;
  // 작성시각은 닉네임 옆에서 `monthDayTime` 으로 적는다. 예전에는 여기서 "오늘이면
  // 시:분, 아니면 연.월.일" 문자열을 만들어 뒀는데 **어디에도 그리지 않았다**(eslint 가
  // 안 쓰인다고 알려 줬다). 상세 화면에서는 날짜를 숨겨서 얻을 게 없어 지웠다.
  const isOwner = user.nickname === tactic.nickname;
  const isAdmin = isOperator(user);
  // 내 룬으로 맞추기 — 이 공략 공덱 룬작에 내 룬을 룬 옵티마이저 [룬피팅] 탭에서 맞춘다 (사용자 2026-10-27). [공략] 탭에도 (사용자 2026-10-28)
  const fitButton = (tactic.attackTeam || []).length === 3 && [0, 1, 2].some((i) => hasBuild(tactic.builds?.attack?.[i] as TacticBuild)) ? (
    <button
      type="button"
      onClick={() => {
        openRuneFit({ label: `공략 「${tactic.title || '제목 없음'}」 공덱`, mobs: (tactic.attackTeam || []).slice(0, 3), builds: [0, 1, 2].map((i) => (tactic.builds?.attack?.[i] as TacticBuild) || null) });
        onClose();
      }}
      style={{ marginTop: 10, width: '100%', padding: '10px 0', borderRadius: 12, border: '1.5px solid rgba(102, 126, 234, 0.6)', background: 'rgba(102, 126, 234, 0.14)', color: '#667eea', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}
    >내 룬으로 맞추기</button>
  ) : null;

  // 휴대폰은 팝업이 아니라 전체 화면 (사용자 2026-10-28) — 높이는 inset 0(100vh 는 주소창만큼 아래가 잘린다).
  // 안 상자에 border-radius 를 적지 않는다 — index.css 가 '화면을 덮는 창 안의 둥근 상자'에 보라 테두리를 둘러 팝업처럼 보였다
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      background: 'rgba(0, 0, 0, 0.5)',
      padding: 0
    }}>
      <div style={{
        background: '#fff',
        width: '100%',
        height: '100%',
        padding: 16,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        zIndex: 2,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }} className="fullscreen-scroll" onClick={e => e.stopPropagation()}>
        {/* 상단 버튼들 */}
        {/* 위 단추 줄 — 창 맨 위 가로 전체에 사이트 보라 띠(사용자 2026-10-27 "배경을 칠하자") */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          zIndex: 10
        }}>
          {/* 뒤로가기 — 동그란 화살표(components/BackButton). 보라 띠 위라 반투명 흰 동그라미(사용자 2026-10-27, 공지 팝업과 같게) */}
          <BackButton onClick={handleClose} size={32} style={{ background: 'rgba(255, 255, 255, 0.22)', boxShadow: 'none', border: 'none' }} />
          {/* 제목 — 보라 띠 안 (사용자 2026-10-28 "제목을 맨 위에다가 올려버리자"). 고칠 때는 아래 입력 칸 */}
          {!editMode && (
            <div title={tactic.title} style={{
              flex: 1, minWidth: 0, alignSelf: 'center', margin: '0 10px', fontSize: 16, fontWeight: 800, color: '#fff',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left'
            }}>{tactic.title}</div>
          )}

          <div style={{ display: 'flex', gap: 4 }}>
            {/* 더보기 메뉴 */}
            {(isOwner || isAdmin) && (
              <div style={{ position: 'relative' }} ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(v => !v)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    fontSize: 18,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.transform = 'scale(0.95)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  ⋯
                </button>
                {dropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    minWidth: 80,
                    zIndex: 100,
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}>
                    {(isOwner || isAdmin) && (
                      <button
                        onClick={handleEditClick}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: 14,
                          textAlign: 'left',
                          color: '#333',
                          fontWeight: 500,
                          transition: 'all 0.2s ease'
                        }}
                        onTouchStart={(e) => {
                          e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                          e.currentTarget.style.color = '#667eea';
                        }}
                        onTouchEnd={(e) => {
                          e.currentTarget.style.background = 'none';
                          e.currentTarget.style.color = '#333';
                        }}
                      >
                        수정
                      </button>
                    )}
                    {(isOwner || isAdmin) && (
                      <button
                        onClick={handleDeleteTactic}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: 14,
                          color: '#d32f2f',
                          textAlign: 'left',
                          fontWeight: 500,
                          transition: 'all 0.2s ease'
                        }}
                        onTouchStart={(e) => {
                          e.currentTarget.style.background = 'rgba(211, 47, 47, 0.1)';
                        }}
                        onTouchEnd={(e) => {
                          e.currentTarget.style.background = 'none';
                        }}
                      >
                        삭제
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* URL 복사 버튼 */}
            <button
              onClick={() => {
                const url = `${window.location.origin}/tactic/${tactic._id}`;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(url).then(() => {
                    alert('공략 URL이 복사되었습니다!');
                  }).catch(() => {
                    alert('URL 복사에 실패했습니다.');
                  });
                } else {
                  const textArea = document.createElement('textarea');
                  textArea.value = url;
                  document.body.appendChild(textArea);
                  textArea.select();
                  try {
                    document.execCommand('copy');
                    alert('공략 URL이 복사되었습니다!');
                  } catch (err) {
                    alert('URL 복사에 실패했습니다.');
                  }
                  document.body.removeChild(textArea);
                }
              }}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.transform = 'scale(0.95)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <IconLink />
            </button>
          </div>
        </div>

        {/* 제목 */}
        <>
          <div style={{
            marginBottom: editMode ? 12 : 0,
            marginTop: 56,
            width: '100%'
          }}>
            {editMode ? (
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                style={{
                  width: '100%',
                  fontSize: 18,
                  fontWeight: 800,
                  background: '#fff',
                  border: '2px solid #667eea',
                  borderRadius: 8,
                  padding: '6px 10px',
                  color: '#000',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                  boxSizing: 'border-box'
                }}
                maxLength={40}
                autoFocus
              />
            ) : null}
          </div>


            {/* 프로필/닉네임/작성일 */}
            <div style={{
              width: '100%',
              marginBottom: 16
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                width: '100%',
                background: '#fff',
                padding: '12px 0',
                borderBottom: '1px solid #e0e0e0'
              }}>
                {/* 프로필 이미지 + 닉네임/조회수 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {/* 네모난 프로필 이미지 */}
                  <img loading="lazy"
                    src={
                      tactic.profileImg || '/images/default-profile.png'
                    }
                    alt="프로필"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 6,
                      objectFit: 'cover',
                      border: '2px solid rgba(102, 126, 234, 0.3)',
                      background: '#fff'
                    }}
                    onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                  />

                  {/* 닉네임과 조회수 */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: tactic.isDeleted ? '#ff6b6b' : '#1976d2',
                      textAlign: 'left'
                    }}>
                      {tactic.nickname}
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: '#888',
                      fontWeight: 500,
                      textAlign: 'left'
                    }}>
                      {/* 작성시간도 같이. 칸 이름이 "프로필/닉네임/작성일" 인데 정작
                          작성일이 없었다 */}
                      조회수 {typeof tactic.views === 'number' ? tactic.views : 0}
                      {tactic.createdAt && ` · ${monthDayTime(tactic.createdAt, false)}`}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </>

        {/* 공덱/방덱 */}
        <div style={{
          width: '100%',
          marginBottom: 16,
          paddingBottom: 16,
          borderBottom: '1px solid #e0e0e0'
        }}>
          {editMode ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              width: '100%',
              alignItems: 'center'
            }}>
              {/* 공덱 선택 */}
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  {editAttackTeam.map((monster, idx) => (
                    <div key={idx} className={tab === 'runes' && runeIdx === idx ? 'mob-picked' : undefined} style={{ position: 'relative', cursor: 'pointer', ...(tab === 'runes' ? { borderRadius: 10 } : {}) }} onClick={() => {
                      if (tab === 'runes') { setRuneIdx(idx); return; }
                      setEditingTeam('attack');
                      setEditingSlot(idx);
                      setMonsterSelectOpen(true);
                    }}>
                      {monster.name ? (
                        <>
                          <div style={{
                            width: 48,
                            height: 48,
                            border: '2px solid #1976d2',
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            position: 'relative'
                          }}>
                            <img loading="lazy"
                              src={`/mobs/v2/${getMonsterImgPath(monsterMapping, monster).imgPath?.replace(/\\/g, '/')}`}
                              alt={monster.name}
                              style={{ width: 44, height: 44, objectFit: 'contain' }}
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMonsterRemove('attack', idx);
                            }}
                            style={{
                              position: 'absolute',
                              top: -5,
                              right: -5,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              background: '#f44336',
                              color: '#fff',
                              border: 'none',
                              fontSize: 10,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >×</button>
                        </>
                      ) : (
                        <div style={{
                          width: 48,
                          height: 48,
                          border: '2px dashed #1976d2',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#1976d2',
                          fontSize: 20
                        }}>+</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* 방덱 선택 */}
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  {editDefenseTeam.map((monster, idx) => (
                    <div key={idx} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => {
                      setEditingTeam('defense');
                      setEditingSlot(idx);
                      setMonsterSelectOpen(true);
                    }}>
                      {monster.name ? (
                        <>
                          <div style={{
                            width: 48,
                            height: 48,
                            border: '2px solid #d32f2f',
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            position: 'relative'
                          }}>
                            <img loading="lazy"
                              src={`/mobs/v2/${getMonsterImgPath(monsterMapping, monster).imgPath?.replace(/\\/g, '/')}`}
                              alt={monster.name}
                              style={{ width: 44, height: 44, objectFit: 'contain' }}
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMonsterRemove('defense', idx);
                            }}
                            style={{
                              position: 'absolute',
                              top: -5,
                              right: -5,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              background: '#f44336',
                              color: '#fff',
                              border: 'none',
                              fontSize: 10,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >×</button>
                        </>
                      ) : (
                        <div style={{
                          width: 48,
                          height: 48,
                          border: '2px dashed #d32f2f',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#d32f2f',
                          fontSize: 20
                        }}>+</div>
                      )}
                    </div>
                  ))}
                </div>
                <ExtraDefenseRows rows={editExtra} mapping={monsterMapping} size={26} editable
                  onRemoveRow={(r) => setEditExtra((rows) => rows.filter((_, i) => i !== r))} />
                {/* 통계의 상대 방덱에서 가져오기 — 방덱 칸 아래(사용자) */}
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                  <ImportDefenseButton rows={editExtra} onChange={setEditExtra} main={editDefenseTeam} onMain={setEditDefenseTeam} mapping={monsterMapping} isMobile={true} />
                </div>
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              width: '100%',
              alignItems: 'center'
            }}>
              {/* 공덱 · VS · 방덱 한 줄(사용자 2026-10-27) */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 6, width: '100%' }}>
              {/* 공덱 */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  {(tactic.attackTeam || []).map((m: any, idx: number) => {
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 3 }}>
                      {/* 공덱 몹을 누르면 [공략] 탭에서도 바로 [룬 정보]로 넘어가 그 몹 룬을 보인다 (사용자 2026-10-28) */}
                      <div onClick={() => { setRuneIdx(idx); setTab('runes'); }} title="이 몹의 룬 보기" className={tab === 'runes' && runeIdx === idx ? 'mob-picked' : undefined} style={{ position: 'relative', width: 44, height: 44, cursor: 'pointer', borderRadius: 12 }}>
                        {imgPath ? (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              padding: 0,
                              background: 'linear-gradient(270deg, #1976d2, #42a5f5, #90caf9, #1976d2)',
                              backgroundSize: '400% 400%',
                              animation: 'blue-border 3s linear infinite',
                              position: 'relative',
                            }}
                          >
                            <img loading="lazy"
                              src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`}
                              alt={displayName}
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: 'contain',
                                borderRadius: 6,
                                margin: 2,
                                background: '#fff',
                                display: 'block',
                                boxShadow: '0 2px 8px #1976d222',
                                transition: 'opacity 0.3s',
                              }}
                            />
                          </span>
                        ) : (
                          <div style={{ width: 44, height: 44, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, fontSize: 11, color: '#aaa', border: '1.5px solid #ddd' }}>
                            없음
                          </div>
                        )}
                      </div>
                      {imgPath ? (
                        <div style={{ fontSize: 9, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* VS — 몹 그림 높이의 가운데 */}
            <div style={{ height: 44, display: 'flex', alignItems: 'center', fontSize: 22, fontWeight: 900, color: '#667eea', letterSpacing: 0.5, flexShrink: 0 }}>VS</div>
            {/* 방덱 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                {(tactic.defenseTeam || []).map((m: any, idx: number) => {
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 3 }}>
                      <div style={{ position: 'relative', width: 44, height: 44 }}>
                        {imgPath ? (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              padding: 0,
                              background: 'linear-gradient(270deg, #d32f2f, #ff5252, #ffb4b4, #d32f2f)',
                              backgroundSize: '400% 400%',
                              animation: 'red-border 3s linear infinite',
                              position: 'relative',
                            }}
                          >
                            <img loading="lazy"
                              src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`}
                              alt={displayName}
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: 'contain',
                                borderRadius: 6,
                                margin: 2,
                                background: '#fff',
                                display: 'block',
                                boxShadow: '0 2px 8px #d32f2f22',
                                transition: 'opacity 1s',
                              }}
                            />
                          </span>
                        ) : (
                          <div style={{ width: 44, height: 44, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, fontSize: 11, color: '#aaa', border: '1.5px solid #ddd' }}>
                            없음
                          </div>
                        )}
                      </div>
                      {imgPath ? (
                        <div style={{ fontSize: 9, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
            {/* 추가 방덱은 줄 아래로 */}
            <ExtraDefenseRows rows={(tactic as any).extraDefenses} mapping={monsterMapping} size={26} editable={false} />
          </div>
          )}
        </div>

        {/* [공략] / [룬 정보] 탭 — 수정 중에도 그대로 둔다(룬 설정은 [룬 정보] 탭에서 고친다) */}
        {(
          <div style={{ display: 'flex', gap: 6, width: '100%', marginBottom: 14, borderBottom: '1px solid #eee' }}>
            {([['tactic', '공략'], ['runes', '룬 정보']] as Array<['tactic' | 'runes', string]>).map(([k, label]) => (
              <button
                key={k} type="button"
                onClick={() => {
                  setTab(k);
                  // 룬 정보로 오면 세팅이 있는 첫 공덱 몹을 고른다
                  if (k === 'runes') {
                    const first = [0, 1, 2].find((i) => hasBuild(tactic.builds?.attack?.[i] as TacticBuild));
                    setRuneIdx(first ?? 0);
                  }
                }}
                style={{
                  padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: 13, fontWeight: 800, color: tab === k ? '#667eea' : '#aaa',
                  borderBottom: tab === k ? '3px solid #667eea' : '3px solid transparent', marginBottom: -1
                }}
              >{label}</button>
            ))}
          </div>
        )}

        {tab === 'runes' && (
          <div style={{ width: '100%', marginBottom: 20 }}>
            {editMode ? (
              <>
                {/* 수정 중 — 고친 세팅은 게시글을 저장할 때 함께 저장된다. 위 공덱 칸을 누르면 몹이 바뀐다 */}
                <BuildRunes
                  build={editBuilds[runeIdx] || null}
                  mob={editAttackTeam[runeIdx]?.name ? editAttackTeam[runeIdx] : null}
                  mapping={monsterMapping}
                  isMobile={true}
                  onSave={(nb) => setEditBuilds((bs) => bs.map((x, i) => (i === runeIdx ? nb : x)))}
                  applyLabel="적용"
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 14 }}>
                  <button
                    type="button" disabled={editLoading}
                    onClick={() => { setEditMode(false); setEditError(''); }}
                    style={{ padding: '6px 14px', borderRadius: 12, border: '2px solid #667eea', background: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >취소</button>
                  <button
                    type="button" disabled={editLoading}
                    onClick={handleEditSave}
                    style={{ padding: '6px 14px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >저장</button>
                </div>
                {editError && <div style={{ color: '#d32f2f', marginTop: 8, fontSize: 11, fontWeight: 500, textAlign: 'right' }}>{editError}</div>}
              </>
            ) : (
              <>
                <BuildRunes
                  build={(tactic.builds?.attack?.[runeIdx] as TacticBuild) || null}
                  mob={(tactic.attackTeam || [])[runeIdx]}
                  mapping={monsterMapping}
                  isMobile={true}
                />
                {/* 룬 변경은 공략 [수정] 중에만 — 보기에서는 단추를 두지 않는다 (사용자 2026-10-28) */}
                {/* 내 룬으로 맞추기 — 이 공략 공덱 룬작에 내 룬을 룬 옵티마이저 [룬피팅] 탭에서 맞춘다 (사용자 2026-10-27) */}
                {fitButton}
              </>
            )}
          </div>
        )}

        {tab === 'tactic' && (<>
        {/* 게시글 본문 */}
        <div style={{ width: '100%', marginBottom: 16 }}>
          {editMode ? (
            <div style={{ position: 'relative', width: '100%' }}>
              <textarea
                value={editComment}
                onChange={e => setEditComment(e.target.value)}
                style={{
                  fontSize: 14,
                  color: '#333',
                  minHeight: 200,
                  width: '100%',
                  padding: '16px',
                  resize: 'vertical',
                  transition: 'all 0.3s ease',
                  fontFamily: 'inherit',
                  lineHeight: 1.6,
                  borderRadius: 12,
                  border: '2px solid #e0e0e0',
                  background: '#fff'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                maxLength={1000}
              />
              {editError && <div style={{ color: '#d32f2f', marginTop: 8, fontSize: 12, fontWeight: 500 }}>{editError}</div>}
            </div>
          ) : (
            <div style={{
              background: '#fff',
              padding: '16px 0',
              fontSize: 14,
              color: '#333',
              lineHeight: 1.7,
              textAlign: 'left',
              minHeight: 200,
              width: '100%',
              whiteSpace: 'pre-line',
              maxHeight: `calc(1.7em * 14)`,
              overflowY: 'auto',
              borderBottom: '1px solid #e0e0e0'
            }}>
              {tactic.comment}
            </div>
          )}
        </div>

        {/* 저장/취소 버튼 */}
        {editMode && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginBottom: 16 }}>
            <button
              onClick={() => {
                setEditMode(false);
                setEditError('');
              }}
              disabled={editLoading}
              style={{
                background: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                border: '2px solid #667eea',
                borderRadius: 8,
                padding: '6px 16px',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                opacity: editLoading ? 0.5 : 1,
                transition: 'all 0.3s ease'
              }}
            >취소</button>
            <button
              onClick={handleEditSave}
              disabled={editLoading}
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '6px 16px',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                opacity: editLoading ? 0.7 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
            >저장</button>
          </div>
        )}
        {!editMode && fitButton}

        </>)}

      </div>
      {/* 몬스터 선택 모달 */}
      {monsterSelectOpen && (
        <MonsterSelect
          open={monsterSelectOpen}
          monsterMapping={monsterMapping}
          onClose={() => setMonsterSelectOpen(false)}
          onSelect={handleMonsterSelect}
        />
      )}
    </div>
  );
};

export default TacticPost;
